import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, GraduationCap, Phone, Mail, PenLine, Camera, Upload, Trash, Star, MapPinOff } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/auth";
import { logError } from "@/lib/log";
import SignaturePad from "@/components/SignaturePad";

// Activos sin dirección: no se pueden ordenar por cercanía en la asignación
const needsAddress = (i) =>
  i.active !== false
  && !(i.address || "").trim()
  && !`${i.name} ${i.surname}`.toUpperCase().includes("PENDIENTE BUSCAR");

const MissingAddressNotice = ({ items, onEdit }) => {
  const missing = items.filter(needsAddress);
  if (!missing.length) return null;
  return (
    <div
      className="mb-4 rounded-lg border-2 border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
      data-testid="instructors-missing-address"
    >
      <div className="flex items-center gap-2 font-bold">
        <MapPinOff className="h-4 w-4" />
        {missing.length === 1
          ? "1 instructor activo sin dirección postal"
          : `${missing.length} instructores activos sin dirección postal`}
      </div>
      <p className="mt-1 text-xs">
        Sin ella no se pueden ordenar por cercanía al asignar un curso. Pulsa
        en un nombre para añadirla.
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {missing.map((i) => (
          <button
            key={i.id}
            type="button"
            onClick={() => onEdit(i)}
            className="rounded-md border border-amber-400 bg-white px-2 py-1 text-xs font-semibold hover:bg-amber-100 transition-colors"
            data-testid={`missing-address-edit-${i.id}`}
          >
            {i.name} {i.surname}
          </button>
        ))}
      </div>
    </div>
  );
};

const emptyForm = { name: "", surname: "", dni: "", phone: "", email: "", invoice_type: "", address: "", iban: "", hourly_rate: "25", km_rate: "0.20", password: "" };

const INVOICE_TYPES = ["AUTONOMO", "COLABORADOR"];

export default function InstructorsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Firma maestra del instructor — gestor puede subir/dibujar/eliminar
  const [signatureModal, setSignatureModal] = useState(null);
  // signatureModal = { instructor, mode: "chooser" | "draw", preview: dataUrl|null, saving: bool }

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/instructors");
      const list = (res.data || []).slice().sort((a, b) => {
        // Orden alfabético por nombre + apellido (case-insensitive, es-ES)
        const na = `${a.name || ""} ${a.surname || ""}`.trim();
        const nb = `${b.name || ""} ${b.surname || ""}`.trim();
        return na.localeCompare(nb, "es", { sensitivity: "base" });
      });
      setItems(list);
    } catch (e) {
      logError("No se pudieron cargar los instructores", e);
      toast.error("No se pudieron cargar los instructores.");
    } finally {
      setLoading(false);
    }
    // adminApi/logError/toast son imports estables; setters de useState
    // tambien lo son. Lint silenciado intencionalmente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const toggleActive = async (ins) => {
    const next = ins.active === false;
    try {
      await adminApi.post(
        `/instructors/${ins.id}/active?active=${next ? "true" : "false"}`
      );
      toast[next ? "success" : "warning"](
        next
          ? `${ins.name} vuelve a entrar en el cálculo de instructores.`
          : `${ins.name} queda fuera del cálculo de instructores.`
      );
      fetchItems();
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "No se pudo cambiar el estado del instructor."
      );
    }
  };

  const openEdit = (i) => {
    setEditingId(i.id);
    setForm({
      name: i.name || "",
      surname: i.surname || "",
      dni: i.dni || "",
      phone: i.phone || "",
      email: i.email || "",
      invoice_type: i.invoice_type || "",
      address: i.address || "",
      iban: i.iban || "",
      hourly_rate:
        i.hourly_rate === null || i.hourly_rate === undefined
          ? ""
          : String(i.hourly_rate),
      km_rate:
        i.km_rate === null || i.km_rate === undefined
          ? ""
          : String(i.km_rate),
      password: "", // empty unless the gestor wants to set a new one
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("El nombre es obligatorio.");
      return;
    }
    setSaving(true);
    try {
      const parseRate = (raw) => {
        const v = String(raw ?? "").trim().replace(",", ".");
        if (v === "") return null;
        const n = parseFloat(v);
        return Number.isFinite(n) && n >= 0 ? n : null;
      };
      const payload = {
        name: form.name.trim(),
        surname: form.surname.trim(),
        dni: form.dni.trim().toUpperCase(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        invoice_type: form.invoice_type || "",
        address: form.address.trim(),
        iban: form.iban.trim().toUpperCase(),
        hourly_rate: parseRate(form.hourly_rate),
        km_rate: parseRate(form.km_rate),
      };
      if (form.password) payload.password = form.password;
      if (editingId) {
        await adminApi.put(`/instructors/${editingId}`, payload);
        toast.success("Instructor actualizado.");
      } else {
        await adminApi.post("/instructors", payload);
        toast.success("Instructor creado.");
      }
      setDialogOpen(false);
      await fetchItems();
    } catch (e) {
      toast.error("Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminApi.delete(`/instructors/${deleteTarget.id}`);
      toast.success("Instructor eliminado.");
      setDeleteTarget(null);
      await fetchItems();
    } catch (e) {
      toast.error("No se pudo eliminar.");
    }
  };

  // --------------------- Firma maestra ---------------------
  const openSignature = async (instructor) => {
    // Pre-cargamos la firma actual si existe para mostrarla como preview
    try {
      const res = await adminApi.get(`/instructors/${instructor.id}/signature`);
      setSignatureModal({
        instructor,
        mode: "chooser",
        preview: res.data?.signature_base64 || null,
        saving: false,
      });
    } catch (e) {
      logError("No se pudo cargar la firma actual", e);
      setSignatureModal({
        instructor,
        mode: "chooser",
        preview: null,
        saving: false,
      });
    }
  };

  const saveSignatureDataUrl = async (dataUrl) => {
    if (!signatureModal) return;
    setSignatureModal((s) => ({ ...s, saving: true }));
    try {
      await adminApi.post(
        `/instructors/${signatureModal.instructor.id}/signature`,
        { signature_base64: dataUrl }
      );
      toast.success("Firma guardada.");
      setSignatureModal(null);
      await fetchItems();
    } catch (e) {
      logError("No se pudo guardar la firma", e);
      toast.error(e.response?.data?.detail || "No se pudo guardar la firma.");
      setSignatureModal((s) => (s ? { ...s, saving: false } : s));
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // permite re-seleccionar el mismo archivo
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("El archivo debe ser una imagen (JPG/PNG/HEIC).");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("La imagen es demasiado grande (máximo 8 MB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const result = ev.target?.result;
      if (typeof result === "string") {
        await saveSignatureDataUrl(result);
      } else {
        toast.error("No se pudo leer la imagen.");
      }
    };
    reader.onerror = () => toast.error("No se pudo leer la imagen.");
    reader.readAsDataURL(file);
  };

  const deleteSignature = async () => {
    if (!signatureModal) return;
    if (!window.confirm("¿Eliminar la firma maestra de este instructor?")) {
      return;
    }
    setSignatureModal((s) => ({ ...s, saving: true }));
    try {
      await adminApi.delete(
        `/instructors/${signatureModal.instructor.id}/signature`
      );
      toast.success("Firma eliminada.");
      setSignatureModal(null);
      await fetchItems();
    } catch (e) {
      toast.error("No se pudo eliminar la firma.");
      setSignatureModal((s) => (s ? { ...s, saving: false } : s));
    }
  };

  return (
    <section data-testid="instructors-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="heading-font text-2xl font-bold text-zinc-950">
            Instructores
          </h2>
          <p className="text-sm text-zinc-600 mt-1">
            {items.length}{" "}
            {items.length === 1 ? "instructor" : "instructores"}
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="h-11 px-5 bg-zinc-950 hover:bg-zinc-800 text-white rounded-md"
          data-testid="new-instructor-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuevo instructor
        </Button>
      </div>

      {loading ? (
        <div className="bg-white border border-zinc-200 rounded-lg p-10 text-center text-zinc-500">
          Cargando…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center bg-zinc-100 rounded-md mb-4">
            <GraduationCap className="h-5 w-5 text-zinc-500" />
          </div>
          <h3 className="heading-font text-xl font-bold text-zinc-950">
            Sin instructores
          </h3>
          <p className="text-sm text-zinc-600 mt-2">
            Añade los instructores que impartirán los cursos.
          </p>
        </div>
      ) : (
        <>
        <MissingAddressNotice items={items} onEdit={openEdit} />
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-zinc-200 bg-zinc-50 text-xs uppercase tracking-[0.14em] text-zinc-500 font-semibold">
            <div className="col-span-3">Nombre</div>
            <div className="col-span-2">Teléfono</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-2">Factura</div>
            <div className="col-span-2 text-right">Acciones</div>
          </div>
          {items.map((i) => (
            <div
              key={i.id}
              className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-3 border-b border-zinc-100 last:border-b-0 items-center"
              data-testid={`instructor-row-${i.id}`}
            >
              <div className="md:col-span-3">
                <div className="font-semibold text-zinc-950">
                  {i.name} {i.surname}
                </div>
                {needsAddress(i) && (
                  <span
                    className="mt-1 inline-flex items-center gap-1 rounded-sm border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800"
                    data-testid={`instructor-no-address-${i.id}`}
                  >
                    <MapPinOff className="h-3 w-3" />
                    Sin dirección
                  </span>
                )}
              </div>
              <div className="md:col-span-2 text-sm text-zinc-700 inline-flex items-center gap-1.5 font-mono">
                {i.phone ? (
                  <>
                    <Phone className="h-3.5 w-3.5 text-zinc-400" />
                    {i.phone}
                  </>
                ) : (
                  <span className="text-zinc-400">—</span>
                )}
              </div>
              <div className="md:col-span-3 text-sm text-zinc-700 inline-flex items-center gap-1.5 truncate">
                {i.email ? (
                  <>
                    <Mail className="h-3.5 w-3.5 text-zinc-400" />
                    <span className="truncate">{i.email}</span>
                  </>
                ) : (
                  <span className="text-zinc-400">—</span>
                )}
              </div>
              <div
                className="md:col-span-2 text-sm"
                data-testid={`instructor-invoice-${i.id}`}
              >
                {i.invoice_type ? (
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold uppercase tracking-wide border ${
                      i.invoice_type === "AUTONOMO"
                        ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                        : "bg-sky-50 text-sky-800 border-sky-200"
                    }`}
                  >
                    {i.invoice_type}
                  </span>
                ) : (
                  <span className="text-zinc-400">—</span>
                )}
              </div>
              <div className="md:col-span-2 flex items-center justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openSignature(i)}
                  className={`h-9 border ${
                    i.has_signature
                      ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                      : "border-zinc-300 hover:bg-zinc-50"
                  }`}
                  data-testid={`signature-instructor-${i.id}`}
                  title={i.has_signature ? "Firma guardada — gestionar" : "Añadir firma"}
                  aria-label="Gestionar firma del instructor"
                >
                  <PenLine className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEdit(i)}
                  className="h-9 border border-zinc-300 hover:bg-zinc-50"
                  data-testid={`edit-instructor-${i.id}`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toggleActive(i)}
                  className={`h-9 border ${
                    i.active === false
                      ? "border-zinc-300 text-zinc-400 hover:bg-zinc-50"
                      : "border-amber-300 bg-amber-50 text-amber-600 hover:bg-amber-100"
                  }`}
                  data-testid={`toggle-active-instructor-${i.id}`}
                  title={
                    i.active === false
                      ? "Instructor apagado: no entra en el cálculo de Asignación de instructor"
                      : "Instructor activo: entra en el cálculo de Asignación de instructor"
                  }
                  aria-label="Instructor activo"
                >
                  <Star
                    className="h-3.5 w-3.5"
                    fill={i.active === false ? "none" : "currentColor"}
                  />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDeleteTarget(i)}
                  className="h-9 border border-zinc-300 hover:bg-red-50 hover:border-red-300 hover:text-red-700"
                  data-testid={`delete-instructor-${i.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="instructor-dialog">
          <DialogHeader>
            <DialogTitle className="heading-font text-2xl">
              {editingId ? "Editar instructor" : "Nuevo instructor"}
            </DialogTitle>
            <DialogDescription>
              Datos de contacto del instructor.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Nombre
                </label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value.toUpperCase() })}
                  className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                  data-testid="instructor-name-input"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Apellidos
                </label>
                <Input
                  value={form.surname}
                  onChange={(e) =>
                    setForm({ ...form, surname: e.target.value.toUpperCase() })
                  }
                  className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                  data-testid="instructor-surname-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                DNI{" "}
                <span className="text-zinc-500 font-normal">
                  (aparecerá en el certificado ISSEFE)
                </span>
              </label>
              <Input
                value={form.dni}
                onChange={(e) =>
                  setForm({ ...form, dni: e.target.value.toUpperCase() })
                }
                placeholder="12345678A"
                maxLength={9}
                className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0 tracking-wider"
                data-testid="instructor-dni-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Teléfono
              </label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="600000000"
                className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                data-testid="instructor-phone-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Email{" "}
                <span className="text-zinc-500 font-normal">
                  (será su usuario de acceso)
                </span>
              </label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="nombre@dominio.com"
                className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                data-testid="instructor-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Dirección postal{" "}
                <span className="text-zinc-500 font-normal">
                  (para calcular km al lugar del curso)
                </span>
              </label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Calle, número, código postal, ciudad"
                className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                data-testid="instructor-address-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Nº de cuenta (IBAN){" "}
                <span className="text-zinc-500 font-normal">
                  (sale en el recibo de formaciones)
                </span>
              </label>
              <Input
                value={form.iban}
                onChange={(e) => setForm({ ...form, iban: e.target.value })}
                placeholder="ES00 0000 0000 0000 0000 0000"
                className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0 font-mono"
                data-testid="instructor-iban-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Precio por hora{" "}
                  <span className="text-zinc-500 font-normal">(€/h)</span>
                </label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={form.hourly_rate}
                  onChange={(e) =>
                    setForm({ ...form, hourly_rate: e.target.value })
                  }
                  placeholder="50"
                  className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0 font-mono"
                  data-testid="instructor-hourly-rate-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Precio por km{" "}
                  <span className="text-zinc-500 font-normal">(€/km)</span>
                </label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={form.km_rate}
                  onChange={(e) =>
                    setForm({ ...form, km_rate: e.target.value })
                  }
                  placeholder="0.22"
                  className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0 font-mono"
                  data-testid="instructor-km-rate-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Factura
                <span className="text-zinc-500 font-normal ml-1">
                  (tipo de facturación)
                </span>
              </label>
              <Select
                value={form.invoice_type || undefined}
                onValueChange={(v) =>
                  setForm({
                    ...form,
                    invoice_type: v === "__none__" ? "" : v,
                  })
                }
              >
                <SelectTrigger
                  className="h-11 border-2 border-zinc-300 focus:ring-0 focus:border-zinc-900"
                  data-testid="instructor-invoice-select"
                >
                  <SelectValue placeholder="Sin especificar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin especificar</SelectItem>
                  {INVOICE_TYPES.map((t) => (
                    <SelectItem
                      key={t}
                      value={t}
                      data-testid={`instructor-invoice-option-${t}`}
                    >
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Contraseña{" "}
                <span className="text-zinc-500 font-normal">
                  {editingId
                    ? "(dejar en blanco para no cambiarla)"
                    : "(necesaria para que el instructor pueda entrar)"}
                </span>
              </label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) =>
                  setForm({ ...form, password: e.target.value })
                }
                placeholder="••••••••"
                className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                data-testid="instructor-password-input"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={saving}
              className="border border-zinc-300"
              data-testid="instructor-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="instructor-save-button"
            >
              {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent data-testid="delete-instructor-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar instructor?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Los cursos ya creados con este
              instructor mantendrán sus datos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-instructor-cancel">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-instructor-confirm"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Firma maestra del instructor */}
      {signatureModal && signatureModal.mode === "draw" && (
        <SignaturePad
          studentName={`${signatureModal.instructor.name} ${signatureModal.instructor.surname || ""}`.trim()}
          courseName="Firma maestra del instructor"
          saving={signatureModal.saving}
          onCancel={() =>
            setSignatureModal((s) => (s ? { ...s, mode: "chooser" } : s))
          }
          onSave={(dataUrl) => saveSignatureDataUrl(dataUrl)}
        />
      )}

      <Dialog
        open={!!signatureModal && signatureModal.mode === "chooser"}
        onOpenChange={(o) => !o && setSignatureModal(null)}
      >
        <DialogContent
          className="sm:max-w-md"
          data-testid="instructor-signature-dialog"
        >
          <DialogHeader>
            <DialogTitle className="heading-font text-2xl">
              Firma de {signatureModal?.instructor.name}{" "}
              {signatureModal?.instructor.surname}
            </DialogTitle>
            <DialogDescription>
              Esta firma se inserta automáticamente en los certificados ISSEFE
              de todos los cursos que imparta este instructor.
            </DialogDescription>
          </DialogHeader>

          {signatureModal?.preview ? (
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-[0.14em] text-zinc-500 font-semibold">
                Firma actual
              </div>
              <div className="border-2 border-zinc-200 rounded-md bg-white p-3 flex items-center justify-center">
                <img
                  src={signatureModal.preview}
                  alt="Firma actual"
                  className="max-h-40 object-contain"
                  data-testid="instructor-signature-preview"
                />
              </div>
            </div>
          ) : (
            <div className="border-2 border-dashed border-zinc-300 rounded-md py-8 text-center text-zinc-500 text-sm">
              Aún no hay firma guardada.
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 pt-2">
            <Button
              onClick={() =>
                setSignatureModal((s) => (s ? { ...s, mode: "draw" } : s))
              }
              disabled={signatureModal?.saving}
              className="h-12 bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="instructor-signature-draw-button"
            >
              <PenLine className="h-4 w-4 mr-2" />
              Firmar en la pantalla
            </Button>

            <label
              className="h-12 inline-flex items-center justify-center gap-2 rounded-md border-2 border-zinc-900 text-zinc-900 hover:bg-zinc-100 font-semibold cursor-pointer transition-colors"
              data-testid="instructor-signature-photo-label"
            >
              <Camera className="h-4 w-4" />
              Hacer foto o subir imagen
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handlePhotoUpload}
                disabled={signatureModal?.saving}
                data-testid="instructor-signature-photo-input"
              />
            </label>

            <label
              className="h-10 inline-flex items-center justify-center gap-2 rounded-md text-sm text-zinc-600 hover:text-zinc-900 cursor-pointer"
              data-testid="instructor-signature-file-label"
            >
              <Upload className="h-3.5 w-3.5" />
              <span>Elegir archivo del dispositivo</span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoUpload}
                disabled={signatureModal?.saving}
              />
            </label>
          </div>

          <DialogFooter className="gap-2 pt-2 sm:justify-between">
            {signatureModal?.preview ? (
              <Button
                variant="outline"
                onClick={deleteSignature}
                disabled={signatureModal?.saving}
                className="border border-red-200 text-red-700 hover:bg-red-50"
                data-testid="instructor-signature-delete-button"
              >
                <Trash className="h-4 w-4 mr-2" />
                Eliminar firma
              </Button>
            ) : (
              <span />
            )}
            <Button
              variant="outline"
              onClick={() => setSignatureModal(null)}
              disabled={signatureModal?.saving}
              className="border border-zinc-300"
              data-testid="instructor-signature-close-button"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
